import { NgClass } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe } from "@ngx-translate/core";
import {
  Subject,
  catchError,
  finalize,
  isObservable,
  takeUntil,
  throwError,
} from "rxjs";
import { StoreJackpotRepositoryService } from "src/app/pages/store/store-jackpot.service";
import { SyndicateRepositoryService } from "src/app/pages/syndicate/syndicate-repository.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { JackpotTriggersService } from "src/app/services/jackpot-triggers.service";

@Component({
  selector: "app-jackpot-warning",
  templateUrl: "./jackpot-warning.component.html",
  styleUrls: ["./jackpot-warning.component.scss"],
  standalone: true,
  imports: [IonicModule, NgClass, TranslatePipe],
})
export class JackpotWarningComponent implements OnInit, OnDestroy {
  selectedId: number = null;
  translatedData: Data;
  public jackpotValue: string;
  public isDeactivating = false;
  private destroy$ = new Subject<void>();

  constructor(
    private jackpotTriggerService: JackpotTriggersService,
    private syndicateService: SyndicateRepositoryService,
    private storeJackpotService: StoreJackpotRepositoryService,
    private authService: AuthService,
    private matDialogRef: MatDialogRef<JackpotWarningComponent>,
    private GTMSegment: GTMSagmentService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.jackpotTriggerService.storeJackpotData(data);
    try {
      this.authService.userDetails$.pipe(takeUntil(this.destroy$)).subscribe((user: Data) => {
        const languageCode = user?.language_code ? user?.language_code : "en";
        this.jackpotTriggerService?.getTranslatedData(languageCode)?.pipe(takeUntil(this.destroy$)).subscribe(
          (translated) => {
            this.translatedData = translated?.["turn-off-jackpot-trigger"];
          },
          (err) => {
            return err;
          }
        );
      });
    } catch (e) {
      return e;
    }
    this.GTMSegment.GTMPopupInitialization("DeclineInvitaion");
  }

  ngOnInit() {}

  onReveal(selectedId: number) {
    this.selectedId = selectedId;
  }

  close(data?: boolean) {
    if (this.isDeactivating) {
      return;
    }
    this.matDialogRef.close(data);
  }

  turnOff() {
    if (this.isDeactivating) {
      return;
    }

    const trigger = this.data?.trigger;
    const deactivatePayload = this.buildDeactivatePayload(trigger);

    if (!deactivatePayload?.offer_link_id || !deactivatePayload?.offer_id) {
      this.authService.errorHandlerLogs(
        "[JackpotWarning] Missing offer_link_id/offer_id for deactivate",
        {
          offer_link_id: deactivatePayload?.offer_link_id,
          offer_id: deactivatePayload?.offer_id,
          trigger_id: trigger?.id,
        }
      );
      this.showErrorToast("1056", "Process failed");
      this.matDialogRef.close(false);
      return;
    }

    const deactivate$ = this.syndicateService.deactivateTrigger(deactivatePayload);
    if (!isObservable(deactivate$)) {
      this.authService.errorHandlerLogs(
        "[JackpotWarning] Deactivate trigger did not return an Observable",
        deactivate$
      );
      this.showErrorToast("1056", "Process failed");
      this.matDialogRef.close(false);
      return;
    }

    this.isDeactivating = true;
    this.matDialogRef.disableClose = true;

    deactivate$
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.authService.errorHandlerLogs(
            "[JackpotWarning] Deactivate trigger request failed",
            error
          );
          return throwError(() => error);
        }),
        finalize(() => {
          this.isDeactivating = false;
          this.matDialogRef.disableClose = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res || res?.status === "rejected" || res?.status === "fail") {
            this.authService.errorHandlerLogs(
              "[JackpotWarning] Deactivate trigger unsuccessful",
              res
            );
            if (res?.status === "rejected") {
              this.authService
                .getConstantsMsg(res?.error_code)
                .then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: "Something went wrong!",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: "Error",
                    },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((error) => error);
            } else {
              this.showErrorToast("1056", "Process failed");
            }
            this.matDialogRef.close(false);
            return;
          }

          try {
            this.storeJackpotService.markUserJackpotTriggerInactive(trigger?.id);
          } catch (error) {
            this.authService.errorHandlerLogs(
              "[JackpotWarning] Failed to mark trigger inactive locally",
              error
            );
          }

          this.matDialogRef.close(true);

          try {
            this.authService
              .getConstantsMsg("1065")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: `${trigger?.jackpot_name} Jackpot Trigger deactivated`,
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle success.png",
                    type: "success",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => error);

            if (this.GTMSegment) {
              this.GTMSegment.GTMEventPopupInitialization(
                "Trigger Deactivated Via Account"
              );
            }
          } catch (error) {
            this.authService.errorHandlerLogs(
              "[JackpotWarning] Post-deactivate side effect failed",
              error
            );
          }
        },
        error: () => {
          this.showErrorToast("1056", "Process failed");
          this.matDialogRef.close(false);
        },
      });
  }

  private buildDeactivatePayload(trigger: Data) {
    if (!trigger) {
      return null;
    }

    return {
      lines: trigger?.lines,
      jackpot: trigger?.jackpot,
      price: trigger?.price,
      free_jackpot: trigger?.free_jackpot ?? false,
      alternate_free_jackpot: trigger?.alternate_free_jackpot ?? false,
      free_offer_id: trigger?.free_offer_id ?? 0,
      offer_link_id: trigger?.offer_link_id ?? trigger?.prosub_id,
      offer_id: trigger?.offer_id,
    };
  }

  private showErrorToast(code: string, fallbackLine: string) {
    this.authService
      .getConstantsMsg(code)
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: fallbackLine,
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => error);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
